import React from 'react';
import PropTypes from 'prop-types';

import {Stack, Box, Typography, Divider} from 'views/components';
import {translate} from 'views/module';

import ViewEditBio from './view_edit/ViewEditBio';
import Sections from './sections';

import CaregiverAuth from './authentication/CaregiverAuth';
import CaregiverProfileUrl from './profile_url/CaregiverProfileUrl';

import {simplifyQuestions, hydateQestions} from 'utils/user_bio';

const Biography = (props) => {
  const caregiverBioQuestions = props.caregiverBioQuestions;
  const caregiverBackend = props.caregiverBackend;
  const caregiverRn = props.caregiverRn;
  const caregiverDialpadIntegration = props.caregiverDialpadIntegration;
  const caregiverDialpadIntegrationLoading = props.caregiverDialpadIntegrationLoading;
  const [bioQuestions, setBioQuestions] = React.useState({});

  // Load the data
  React.useEffect(() => {
    if (!caregiverBioQuestions?.questions) {
      return;
    }

    const bioQuestions = simplifyQuestions(caregiverBioQuestions.questions);

    setBioQuestions(bioQuestions);
  }, [caregiverBioQuestions]);

  const saveBioQuestions = (fields, onSuccess ) => {
    const bioQuestions = hydateQestions(fields);
    caregiverBackend.bioQuestions.updateMultipleQuestions(bioQuestions, onSuccess);
  };

  return (
    <Stack
      spacing={2}
      sx={{
        margin: 1,
      }}
    >
      <Box
        sx={{
          borderRadius: 3,
          boxShadow: '0px 2px 6px rgba(0,0,0,0.1)',
          marginX: 2,
          paddingX: 2,
          paddingTop: 1.5,
          paddingBottom: 1,
          backgroundColor: 'white',
        }}
      >
        <Typography variant="h6" sx={{fontWeight: 'bold', mb: 1}}>
          {translate('caregiver.CaregiverDetail.Settings.ProfileUrl.Title')}
        </Typography>
        <Divider sx={{mb: 2}} />
        <CaregiverProfileUrl
          {...props}
        />
      </Box>
      <ViewEditBio
        bioQuestions={bioQuestions}
        title={translate('caregiver.CaregiverBiography.Contact.Title')}
        ViewComponent={Sections.ContactView}
        EditComponent={Sections.ContactEdit}
        saveBioQuestions={saveBioQuestions}
        caregiverBackend={caregiverBackend}
        caregiverDialpadIntegration={caregiverDialpadIntegration}
        caregiverDialpadIntegrationLoading={caregiverDialpadIntegrationLoading}
      />
      <ViewEditBio
        bioQuestions={bioQuestions}
        title={translate('caregiver.CaregiverBiography.Profile.Title')}
        ViewComponent={Sections.ProfileView}
        EditComponent={Sections.ProfileEdit}
        saveBioQuestions={saveBioQuestions}
        getSignedFileUrl={caregiverBackend.bioQuestions.getSignedFileUrl}
      />
      <ViewEditBio
        bioQuestions={bioQuestions}
        title={translate('caregiver.CaregiverBiography.Languages.Title')}
        ViewComponent={Sections.LanguagesView}
        EditComponent={Sections.LanguagesEdit}
        saveBioQuestions={saveBioQuestions}
        caregiverRn={caregiverRn}
      />
      <ViewEditBio
        bioQuestions={bioQuestions}
        title={translate('caregiver.CaregiverBiography.Certificate.Title')}
        ViewComponent={Sections.CertificateView}
        EditComponent={Sections.CertificateEdit}
        saveBioQuestions={saveBioQuestions}
        getSignedFileUrl={caregiverBackend.bioQuestions.getSignedFileUrl}
      />
      <ViewEditBio
        bioQuestions={bioQuestions}
        title={translate('caregiver.CaregiverBiography.CanadianId.Title')}
        ViewComponent={Sections.CanadianIdView}
        EditComponent={Sections.CanadianIdEdit}
        saveBioQuestions={saveBioQuestions}
        getSignedFileUrl={caregiverBackend.bioQuestions.getSignedFileUrl}
      />
      <ViewEditBio
        bioQuestions={bioQuestions}
        title={translate('caregiver.CaregiverBiography.VulnerableSectorCheck.Title')}
        ViewComponent={Sections.VulnerableSectorCheckView}
        EditComponent={Sections.VulnerableSectorCheckEdit}
        saveBioQuestions={saveBioQuestions}
        getSignedFileUrl={caregiverBackend.bioQuestions.getSignedFileUrl}
      />
      <ViewEditBio
        bioQuestions={bioQuestions}
        title={translate('caregiver.CaregiverBiography.Education.Title')}
        ViewComponent={Sections.EducationView}
        EditComponent={Sections.EducationEdit}
        saveBioQuestions={saveBioQuestions}
        getSignedFileUrl={caregiverBackend.bioQuestions.getSignedFileUrl}
      />
      <ViewEditBio
        bioQuestions={bioQuestions}
        title={translate('caregiver.CaregiverBiography.WorkHistory.Title')}
        ViewComponent={Sections.WorkHistoryView}
        EditComponent={Sections.WorkHistoryEdit}
        saveBioQuestions={saveBioQuestions}
      />
      <ViewEditBio
        bioQuestions={bioQuestions}
        title={translate('caregiver.CaregiverBiography.References.Title')}
        ViewComponent={Sections.ReferencesView}
        EditComponent={Sections.ReferencesEdit}
        saveBioQuestions={saveBioQuestions}
      />
      <ViewEditBio
        bioQuestions={bioQuestions}
        title={translate('caregiver.CaregiverBiography.PrefsAndAvailability.Title')}
        ViewComponent={Sections.PrefsAndAvailabilityView}
        saveBioQuestions={saveBioQuestions}
        editDisabled={true}
      />
      <ViewEditBio
        bioQuestions={bioQuestions}
        title={translate('caregiver.CaregiverBiography.SkillsAndExp.Title')}
        ViewComponent={Sections.SkillsAndExpView}
        saveBioQuestions={saveBioQuestions}
        editDisabled={true}
      />
      <ViewEditBio
        bioQuestions={bioQuestions}
        title={translate('caregiver.CaregiverBiography.Personality.Title')}
        ViewComponent={Sections.PersonalityView}
        saveBioQuestions={saveBioQuestions}
        editDisabled={true}
      />
      <ViewEditBio
        bioQuestions={bioQuestions}
        title={translate('caregiver.CaregiverBiography.Agreement.Title')}
        ViewComponent={Sections.AgreementView}
        saveBioQuestions={saveBioQuestions}
        editDisabled={true}
      />
      <Box
        sx={{
          borderRadius: 3,
          boxShadow: '0px 2px 6px rgba(0,0,0,0.1)',
          marginX: 2,
          paddingX: 2,
          paddingTop: 1.5,
          paddingBottom: 1,
          backgroundColor: 'white',
        }}
      >
        <Typography variant="h6" sx={{fontWeight: 'bold', mb: 1}}>
          {translate('caregiver.CaregiverDetail.Settings.Auth.Title')}
        </Typography>
        <Divider sx={{mb: 2}} />
        <CaregiverAuth
          {...props}
        />
      </Box>
    </Stack>
  );
};

Biography.propTypes = {
  caregiverRn: PropTypes.string.isRequired,
  refresh: PropTypes.number.isRequired,
  caregiverAuth: PropTypes.object,
  caregiverDialpadIntegration: PropTypes.object,
  caregiverDialpadIntegrationLoading: PropTypes.bool.isRequired,
  caregiverData: PropTypes.object.isRequired,
  caregiverBioQuestions: PropTypes.object.isRequired,
  caregiverBackend: PropTypes.object.isRequired,
  shortIdRecord: PropTypes.object.isRequired,
};
export default React.memo(Biography);

